import { Box, Typography, Chip, type SxProps, type Theme } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import React, { type ReactNode } from 'react';
import { AppCard } from '../../../components/common/AppCard';

// --- Badge ---
// Small label chip with auto-generated light background from color

interface BadgeProps {
    children: ReactNode;
    color?: string;
    bg?: string;
    sx?: SxProps<Theme>;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps & React.HTMLAttributes<HTMLSpanElement>>(
    function Badge({ children, color, bg, sx, ...rest }, ref) {
        const theme = useTheme();
        const c = color || theme.palette.text.secondary;
        return (
            <Box
                ref={ref}
                component="span"
                {...rest}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: '2px',
                    borderRadius: '3px',
                    fontSize: '10.5px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: c,
                    bgcolor: bg || alpha(c, 0.08),
                    lineHeight: '18px',
                    whiteSpace: 'nowrap',
                    ...sx,
                }}
            >
                {children}
            </Box>
        );
    }
);

// --- ProbBar ---
// Probability bar with percentage label

interface ProbBarProps {
    prob: number;
    color?: string;
    width?: number;
}

export function ProbBar({ prob, color, width = 72 }: ProbBarProps) {
    const theme = useTheme();
    const c = color || theme.palette.primary.main;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width,
                    height: 5,
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                    borderRadius: '3px',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: `${prob * 100}%`,
                        height: '100%',
                        bgcolor: c,
                        borderRadius: '3px',
                        transition: 'width 0.4s ease',
                    }}
                />
            </Box>
            <Typography
                component="span"
                sx={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: c,
                    fontWeight: 600,
                    minWidth: 32,
                }}
            >
                {(prob * 100).toFixed(0)}%
            </Typography>
        </Box>
    );
}

// --- StatBox ---
// Icon + label + value vertical stat display

interface StatBoxProps {
    icon: string;
    label: string;
    value: string;
    valueColor?: string;
}

export function StatBox({ icon, label, value, valueColor }: StatBoxProps) {
    const theme = useTheme();
    return (
        <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: '10.5px',
                    color: 'text.secondary',
                    fontWeight: 500,
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    letterSpacing: '0.02em',
                }}
            >
                <Box component="span" sx={{ fontSize: '12px', opacity: 0.6 }}>
                    {icon}
                </Box>{' '}
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '17px',
                    fontWeight: 700,
                    color: valueColor || 'text.primary',
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

// --- RangeBtn ---
// Segmented button group for filters

interface RangeBtnProps {
    options: string[];
    active: string;
    onChange: (val: string) => void;
}

export function RangeBtn({ options, active, onChange }: RangeBtnProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Box sx={{ display: 'flex' }}>
            {options.map((o, i) => (
                <Box
                    key={o}
                    component="button"
                    onClick={() => onChange(o)}
                    sx={{
                        px: 1.75,
                        py: '5px',
                        bgcolor: active === o ? 'text.primary' : 'background.default',
                        color:
                            active === o
                                ? isDark
                                    ? '#000'
                                    : '#fff'
                                : 'text.secondary',
                        border: 1,
                        borderColor: 'divider',
                        borderLeft: i > 0 ? 'none' : undefined,
                        borderRadius:
                            i === 0
                                ? '4px 0 0 4px'
                                : i === options.length - 1
                                    ? '0 4px 4px 0'
                                    : 0,
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.03em',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                        '&:hover': {
                            bgcolor:
                                active === o ? 'text.primary' : 'action.hover',
                        },
                    }}
                >
                    {o}
                </Box>
            ))}
        </Box>
    );
}

// --- PreviewBadge ---
// Subtle "Preview" chip for panels using synthetic data

export function PreviewBadge() {
    const theme = useTheme();
    return (
        <Chip
            label="Preview"
            size="small"
            sx={{
                height: 20,
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                bgcolor: alpha(theme.palette.warning.main, 0.12),
                color: theme.palette.warning.main,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
        />
    );
}

// --- MiniChart ---
// Tiny SVG sparkline

interface MiniChartProps {
    data: number[];
    width?: number;
    height?: number;
    color?: string;
}

export function MiniChart({ data, width = 80, height = 26, color }: MiniChartProps) {
    const theme = useTheme();
    const c = color || theme.palette.primary.main;

    if (!data.length) return null;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const pts = data
        .map(
            (v, i) =>
                `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`,
        )
        .join(' ');

    return (
        <svg width={width} height={height} style={{ display: 'block' }}>
            <polyline
                points={pts}
                fill="none"
                stroke={c}
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
        </svg>
    );
}

// --- SectionCard ---
// Re-export of AppCard for backward compatibility

interface SectionCardProps {
    children: ReactNode;
    id?: string;
    'data-testid'?: string;
    'aria-busy'?: boolean;
    sx?: SxProps<Theme>;
}

export function SectionCard({
    children,
    id,
    'data-testid': testId,
    'aria-busy': busy,
    sx,
}: SectionCardProps) {
    return (
        <AppCard id={id} data-testid={testId} aria-busy={busy} sx={sx}>
            {children}
        </AppCard>
    );
}

// --- PanelHeader ---
// Flex row with title + right-side controls

interface PanelHeaderProps {
    title: string;
    icon?: string;
    children?: ReactNode;
    sx?: SxProps<Theme>;
}

export function PanelHeader({ title, icon, children, sx }: PanelHeaderProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 1.25,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                }}
            >
                {icon && <span>{icon}</span>}
                {title}
            </Typography>
            {children}
        </Box>
    );
}

/**
 * Alert timestamp for the bell rows: HH:MM:SS today, "Mon D HH:MM:SS" older.
 * Returns an em-dash on an unparseable input — normalizeNotificationDoc coerces
 * a non-string alert.ts to '', and "Invalid Date" must never reach a user.
 */
export function formatAlertTimestamp(isoStr: string): string {
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return '—';
    const now = new Date();
    const isToday =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    const time = d.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    if (isToday) return time;
    const date = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
    return `${date} ${time}`;
}
